// @ts-strict-ignore

/**
 * @module DeviceManagement
 */

import { Subject, Subscription, filter } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { WebrtcConfigAndUtils } from 'rainbow-web-sdk/services/webrtc/webrtcConfigAndUtils';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { Service } from 'rainbow-web-sdk/services/service';

/** @internal */
export const DEVICE_MANAGEMENT_SVC = 'DeviceManagementService';

/**
 * @eventProperty
 */
export enum DeviceManagementEvents {

    /**
     * @eventProperty
     * This RB event is send when the application cannot access the audio/video devices. The user should check the app / browser configuration and access to OS devices
     */
    ON_AUDIO_VIDEO_DEVICE_ACCESS_ERROR = "rainbowonaudiovideodeviceaccesserror",

    /**
     * @eventProperty
     * This RB event is send when the application detects any kind of change in the devices list (audio input / output or video input devices)
     * **Event Data**
     * - **kind**: The kind of the device (audioinput, audiooutput or videoinput)
     * - **action**: Could be added (new device is added) / removed (device is removed) / updated (the id of the device has updated)
     */
    ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE = "rainbowonaudiovideodevicelistupdate",

    /**
     * @eventProperty
     * This RB event is send when a new device is selected to be used
     * **Event Data**
     * - **kind**: The kind of the device (audioinput, audiooutput or videoinput)
     * - **plugIn**: True if the new device is selected after a plug-in
     * - **plugOut**: True if the new device is selected after a plug-out of the current device used
     */
    ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED = "rainbowonnewaudiovideodeviceselected",

    /**
     * @eventProperty
     * This RB event is send when a new secondary device is selected to be used; This is used only for incoming calls / IMs that could be played
     * also on the secondary device, if it's activated in the settings
     */
    ON_NEW_SECONDARY_OUTPUT_DEVICE_SELECTED = "rainbowonnewsecondaryoutputdeviceselected"
}

export interface DeviceManagementService {

    /**
     * Return all available cameras
     */
    getAllCameras(): MediaDeviceInfo[];

    /**
     * Return all available microphones
     */
    getAllMicrophones(): MediaDeviceInfo[];

    /**
     * Return all available speakers
     */
    getAllSpeakers(): MediaDeviceInfo[];

    /**
     * Return the current selected microphone (that's used in all VoIP calls)
     */
    getCurrentMicrophone(): MediaDeviceInfo;

    /**
     * Return the current selected speaker (that's used in all VoIP calls)
     */
    getCurrentSpeaker(): MediaDeviceInfo | undefined;

    /**
     * Return the current selected camera (that's used in all VoIP calls)
     */
    getCurrentCamera(): MediaDeviceInfo;

    /**
     * This API can be used to modify the current camera. This will result in sending
     * an update event of type ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED
     * @param camera - The new device to be used; Should be of type videoinput
     */
    setCurrentCamera(camera: MediaDeviceInfo): void;

    /**
     * This API can be used to modify the current speaker. This will result in sending
     * an update event of type DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED
     * @param speaker - The new device to be used; Should be of type audiooutput
     */
    setCurrentSpeaker(speaker: MediaDeviceInfo): void;

    /**
     * This API can be used to modify the current microphone. This will result in sending
     * an update event of type ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED
     * @param microphone - The new device to be used; Should be of type audioinput
     */
    setCurrentMicrophone(microphone: MediaDeviceInfo): void;

    /**
     * Set the output speaker of the audio element with the given speaker.
     * This method is useful if you're developing an audio application and want to configure
     * the speaker to be used by an audio tag in the same way as Rainbow's audio tags.
     * @param element - The Audio element to whom we want to change the output ID
     * @param speaker - The speaker to be used
     */
    setSpeakerForAudioElement(element: HTMLAudioElement, speaker: MediaDeviceInfo): Promise<void>;

    /**
     * Subscribe to updates on the deviceManagement service (all events are of RBEvent type);
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - a list of event names that the user wants to receive when subscribing (used a filter)
     */
    subscribe(handler: (event: RBEvent<DeviceManagementEvents>) => any, eventNames?: DeviceManagementEvents | DeviceManagementEvents[]): Subscription;
}

/**
 * This service manages the input / output audio and video devices used by the application
 * @internal
 */
export class DeviceManagementServiceRB extends Service implements DeviceManagementService {

    private logger: LoggerService = LoggerService.getInstance();
    private webrtcConfigAndUtils: WebrtcConfigAndUtils = WebrtcConfigAndUtils.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private rxSubject: Subject<any> = new Subject<any>();
    private deviceWatcher: any = null;
    private xmppConnectionSubscription: Subscription;
    private audioInputDevices: MediaDeviceInfo[] = [];
    private audioOutputDevices: MediaDeviceInfo[] = [];
    private videoInputDevices: MediaDeviceInfo[] = [];

    private currentAudioInputDevice: MediaDeviceInfo = null;
    private currentAudioOutputDevice: MediaDeviceInfo = null;
    private currentVideoInputDevice: MediaDeviceInfo = null;
    private currentSecondaryOutputDevice: MediaDeviceInfo = null;

    private getUserMediaForAudioDone: boolean = false;
    private getUserMediaForVideoDone: boolean = false;

    public started: boolean = false;

    private manualAleDeviceSelection: boolean = false;

    public static getInstance() {
        return Service.create(DEVICE_MANAGEMENT_SVC, DeviceManagementServiceRB);
    }

    public static build() {
        return new DeviceManagementServiceRB();
    }

    private constructor() { super(); }

    public async start() {
        if (this.started) { return; }
        if (!config.sdk) this.checkDeviceList();

        this.xmppConnectionSubscription = this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
            try {
                if (event.name === 'ON_XMPP_CONNECTED' && event.data.reason === 'RECONNECTION') {
                    this.logger.info("[DeviceManagementService] XMPP Connection state event : RECONNECTION");
                    this.setGlobalTagSinkId(true);
                }
            }
            catch { }
        });

        if (!config.sdk) this.started = true;
    }

    public async stop() {
        if (this.deviceWatcher) this.deviceWatcher = null;
        if (this.xmppConnectionSubscription) { this.xmppConnectionSubscription.unsubscribe(); this.xmppConnectionSubscription = null; }
        if (navigator.mediaDevices?.ondevicechange) navigator.mediaDevices.ondevicechange = null;
        this.started = false;
    }

    /**
    * Subscribe to updates on the deviceManagement service (all events are of RBEvent type);
    * @param handler - The call-back function that will be subscribed to the RxJS subject
    * @param filterEvent - a list of event names that the user wants to receive when subscribing (used a filter)
    */
    public subscribe(handler: (event: RBEvent<DeviceManagementEvents>) => any, eventNames?: DeviceManagementEvents | DeviceManagementEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<DeviceManagementEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;
        return observable.subscribe((event: RBEvent<DeviceManagementEvents>) => { try { handler(event); } catch { } });
    }

    private sendEvent(name: DeviceManagementEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    //list of names that should not be selected automatically when plugged
    private devicesToIgnore = ["display", "screen", "amd ", "monitor", "écran", "lenovo usb audio", "(hdmi)", " dac(", " dac (", "PHL 2", "XSplit ", "DPM4 ", "Audio Control", "NDI Webcam", "Virtual "];
    private inputDevicesToAcceptEvenWhenNotDefault = ["plantronics", "jabra", "konftel", "sennheiser"];

    private arrayContains(array: any[], textSearched: string) {
        return (array.some(function (element) {
            return (textSearched.indexOf(element) > -1);
        }));
    }

    //example of Hash label : "Speakers (Jabra Speak 710) (0b0e:2475)"
    private getHashFromLabel(label: string): string {
        const first = label.lastIndexOf("(");
        const last = label.lastIndexOf(")");
        let result = "";
        if (first && last) {
            result = label.substring(first + 1, last);
            //check if HASH is correct
            if (result.indexOf(":") === -1) {
                result = "";
            }
        }

        return result;
    }

    private compareHashInLabel(labelA: string, labelB: string): boolean {
        const hashA = this.getHashFromLabel(labelA);
        const hashB = this.getHashFromLabel(labelB);

        return (hashA && hashB && hashA === hashB);
    }

    /**
     * Set the output sinkID of the audio element with the output device given
     * @param element - The Audio element to whom we want to change the output ID
     * @param outputDevice - The output device to be used
     */
    public async setSpeakerForAudioElement(element: HTMLAudioElement, outputDevice: MediaDeviceInfo) {
        if (!element) {
            throw new Error("[DeviceManagementService] setSpeakerForAudioElement -- no HTML Audio Element received !");
        }

        if (!element['setSinkId']) {
            //system does not support setSinkId, ignore and do not add logs here
            return;
        }

        if (!outputDevice) {
            this.logger.debug(`[DeviceManagementService] setSpeakerForAudioElement -- no output device or setSinkId does not exist !`);
            return;
        }

        if (element['sinkId'] === outputDevice.deviceId) {
            return;
        }

        try {
            this.logger.debug(`[DeviceManagementService] -- setSpeakerForAudioElement to new device -- ${outputDevice.deviceId}`);

            await element['setSinkId'](outputDevice.deviceId);

            if (element.load) {
                element.load();
            }
        }
        catch (error) {
            this.logger.error(`[DeviceManagementService] -- setSpeakerForAudioElement error -- ${error}`);
            return;
        }
    }

    /**
     * Return the current audio input device selected (that's used in all VoIP calls)
     */
    public getCurrentMicrophone(): MediaDeviceInfo {
        return this.currentAudioInputDevice;
    }

    /**
     * This API can be used to modify the current audio input device. This will result in sending an update event of type ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED
     * @param device - The new device to be used; Should be of type audioinput
     */
    public setCurrentMicrophone(device: MediaDeviceInfo): void {
        if (!device || device.kind !== "audioinput") { throw new Error("[DeviceManagementService] setCurrentMicrophone -- wrong parameter !"); }
        this.currentAudioInputDevice = device;
        this.logger.debug(`[DeviceManagementService] API setCurrentMicrophone ${this.currentAudioInputDevice.deviceId} -- ${this.currentAudioInputDevice.label}`);
        this.settingsService.setSetting("handFreeInputDeviceName", this.currentAudioInputDevice.label);
        this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: device.kind });
    }

    /**
     * Return all available audio input devices (type MediaDeviceInfo)
     */
    public getAllMicrophones(): MediaDeviceInfo[] {
        return this.audioInputDevices;
    }

    /**
     * Return the current audio output device selected (that's used in all VoIP calls)
     */
    public getCurrentSpeaker(): MediaDeviceInfo | undefined {
        return this.currentAudioOutputDevice;
    }

    /**
     * This API can be used to modify the current audio output device. This will result in sending
     * an update event of type DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED
     * @param device - The new device to be used; Should be of type audiooutput
     */
    public setCurrentSpeaker(device: MediaDeviceInfo): void {
        if (!device || device.kind !== "audiooutput") { throw new Error("[DeviceManagementService] setCurrentSpeaker -- wrong parameter !"); }
        this.currentAudioOutputDevice = device;
        this.settingsService.setSetting("handFreeOutputDeviceName", this.currentAudioOutputDevice.label);
        this.logger.debug(`[DeviceManagementService] API setCurrentSpeaker ${this.currentAudioOutputDevice.deviceId} -- ${this.currentAudioOutputDevice.label}`);
        this.setGlobalTagSinkId();
        this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: device.kind });

        //check if new output device is ALE managed, then update the settings
        if (this.isAleManagedDevice(device)) {
            this.updateAleManagedDeviceFlag(device);
        }
    }

    /**
     * Return all available audio output devices (type MediaDeviceInfo)
     */
    public getAllSpeakers(): MediaDeviceInfo[] {
        return this.audioOutputDevices;
    }

    /**
     * Return the current video input device selected (that's used in all VoIP calls)
     */
    public getCurrentCamera(): MediaDeviceInfo {
        return this.currentVideoInputDevice;
    }

    /**
     * This API can be used to modify the currentvideo input device. This will result in sending an update
     * event of type ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED
     * @param device - The new device to be used; Should be of type videoinput
     */
    public setCurrentCamera(device: MediaDeviceInfo): void {
        if (!device || device.kind !== "videoinput") { throw new Error("[DeviceManagementService] setCurrentCamera -- wrong parameter !"); }
        this.currentVideoInputDevice = device;
        this.logger.debug(`[DeviceManagementService] API setCurrentCamera ${this.currentVideoInputDevice.deviceId} -- ${this.currentVideoInputDevice.label}`);
        this.settingsService.setSetting("cameraUsed", this.currentVideoInputDevice.label);
        this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: device.kind });
    }

    /**
     * Return all available video input devices (type MediaDeviceInfo)
     */
    public getAllCameras(): MediaDeviceInfo[] {
        return this.videoInputDevices;
    }

    /**
     * Return the secondary output device to be used for incoming calls / IMs to play sound on
     */
    public getCurrentSecondaryOutputDevice(): MediaDeviceInfo {
        return this.currentSecondaryOutputDevice;
    }

    /**
     * This API can be used to modify the currentvideo input device. This will result in sending
     * an update event of type DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED
     * @param device - The new device to be used; Should be of type audiooutput
     */
    public setCurrentSecondaryOutputDevice(device: MediaDeviceInfo): void {
        if (!device || device.kind !== "audiooutput") { throw new Error("[DeviceManagementService] setCurrentSecondaryOutputDevice -- wrong parameter !"); }
        this.currentSecondaryOutputDevice = device;
        this.logger.debug(`[DeviceManagementService] API setCurrentSecondaryOutputDevice ${this.currentSecondaryOutputDevice.deviceId} -- ${this.currentSecondaryOutputDevice.label}`);
        this.settingsService.setSetting("ringingSpeaker", this.currentSecondaryOutputDevice.label);
        this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED);
    }

    private startDeviceWatcher(): void {
        if (navigator?.mediaDevices && !navigator.mediaDevices.ondevicechange) {
            navigator.mediaDevices.ondevicechange = (event) => {
                this.logger.info("[DeviceManagementService] ondevicechange detected " + JSON.stringify(event));
                if (!this.deviceWatcher) {
                    this.deviceWatcher = setTimeout(() => {
                        this.checkDeviceList();
                        this.deviceWatcher = null;
                    }, 500);
                }
            };
        }
    }

    public async checkDeviceList(): Promise<void> {
        let tempStream = null;
        if (window.adapter && window.adapter.browserDetails.browser === "firefox") {
            //when we're on Firefox, the labels are not shown automatically, so we need to have an active stream
            //to be able to get the labels
            if (this.getUserMediaForVideoDone || this.getUserMediaForAudioDone) {
                //if we've already done the initial get user media, each time there's device update, we need to have active media stream;
                //in case of error, do nothing, but continue (as perhaps the audio stream is already taken by the application)
                try {
                    tempStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
                }
                catch {
                    //do nothing, but continue
                }
            }
        }
        this.logger.info("[DeviceManagementService] checkDeviceList");
        const devices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices();
        this.logger.info("[DeviceManagementService] devices brut list " + JSON.stringify(devices));
        const tempAudioInputDevices: MediaDeviceInfo[] = [];
        const tempAudioOutputDevices: MediaDeviceInfo[] = [];
        const tempVideoInputDevices: MediaDeviceInfo[] = [];

        devices.forEach((device: MediaDeviceInfo) => {
            //check whether we need to get the user media or not
            // Note: For security reasons, the label field is always blank unless an active media stream exists
            // or the user has granted persistent permission for media device access.
            // The set of device labels could otherwise be used as
            // part of a fingerprinting mechanism to identify a user.
            if (device.deviceId && device.label) {
                if (device.kind === "audioinput" || device.kind === "audiooutput") { this.getUserMediaForAudioDone = true; }
                if (device.kind === "videoinput") { this.getUserMediaForVideoDone = true; }
            }

            if (device.kind === "audioinput") { tempAudioInputDevices.push(device); }
            if (device.kind === "audiooutput") { tempAudioOutputDevices.push(device); }
            if (device.kind === "videoinput") { tempVideoInputDevices.push(device); }
        });

        if (!this.getUserMediaForAudioDone && !this.getUserMediaForVideoDone && tempAudioInputDevices.length && tempVideoInputDevices.length) {
            //get user media is not done at all (start-up), so ask for all information
            this.testGetUserMedia(tempAudioInputDevices, tempVideoInputDevices);
            return;
        }

        if (!this.getUserMediaForAudioDone && tempAudioInputDevices.length) {
            //get user media and stop here, as we need to get the information for the input / output devices
            this.testGetUserMedia(tempAudioInputDevices, []);
            return;
        }

        if (!this.getUserMediaForVideoDone && tempVideoInputDevices.length) {
            //get user media and stop here, as we need to get the information for the video input devices
            this.testGetUserMedia([], tempVideoInputDevices);
            return;
        }

        this.manualAleDeviceSelection = this.settingsService.getBooleanSetting("manualAleDeviceSelection");

        //start watching device update, as we've given update on the devices
        this.startDeviceWatcher();

        //check for any changes on the audio input devices
        this.updateCurrentAudioInputDevices(tempAudioInputDevices);
        this.updateCurrentAudioOutputDevices(tempAudioOutputDevices);
        this.updateCurrentVideoInputDevices(tempVideoInputDevices);

        if (tempStream) {
            this.webrtcConfigAndUtils.disableMediaStream(tempStream);
        }
    }

    /*
        Try to get the default audio & video input devices
        In case of error, try to test one by one all audio input and all video input devices
        To see what will happen
    */
    private async testGetUserMedia(audioInputDevices: MediaDeviceInfo[], videoInputDevices: MediaDeviceInfo[]) {
        let shouldTestAudio = true;
        let shouldTestVideo = true;

        if (!audioInputDevices.length) {
            this.logger.info("[DeviceManagementService] testGetUserMedia -- ignore audio test... ");
            shouldTestAudio = false;
        }

        if (!videoInputDevices.length) {
            this.logger.info("[DeviceManagementService] testGetUserMedia -- ignore video test... ");
            shouldTestVideo = false;
        }

        this.logger.info(`[DeviceManagementService] testGetUserMedia for audio ${shouldTestAudio} and video ${shouldTestVideo}`);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: shouldTestAudio, video: shouldTestVideo });
            this.webrtcConfigAndUtils.disableMediaStream(stream);
        }
        catch (error) {
            this.logger.warn(`[DeviceManagementService] testGetUserMedia -- default devices does not work -- ${error}`);
            // check not allowed by system !
            if (error?.indexOf && error.indexOf("NotAllowedError") !== -1) {
                //send event that user have not allowed rainbow
                this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_ACCESS_ERROR);
            }
            else if (error?.toString && error.toString().indexOf("NotAllowedError") !== -1) {
                //send event that user have not allowed rainbow
                this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_ACCESS_ERROR);
            }

            // for (let i = 0; i < audioInputDevices.length; i++) {
            //     try {
            //         const constraints = {
            //             audio: {}
            //         };
            //         constraints["audio"] = {
            //             optional: [
            //                 { sourceId: audioInputDevices[i].deviceId }
            //             ]
            //         };
            //         const stream = await navigator.mediaDevices.getUserMedia(constraints);
            //         this.logger.info(`[DeviceManagementService] testGetUserMedia for audio device ${audioInputDevices[i].deviceId} -- success`);
            //         this.webrtcConfigAndUtils.disableMediaStream(stream);
            //         break;
            //     }
            //     catch (err) {
            //         this.logger.warn(`[DeviceManagementService] testGetUserMedia error for device ${audioInputDevices[i].deviceId} -- ${err}`);
            //     }
            // }

            // for (let i = 0; i < videoInputDevices.length; i++) {
            //     try {
            //         const constraints = {
            //             audio: false,
            //             video: { deviceId:videoInputDevices[i].deviceId }
            //         };
            //         const stream = await navigator.mediaDevices.getUserMedia(constraints);
            //         this.logger.info(`[DeviceManagementService] testGetUserMedia for video device ${videoInputDevices[i].deviceId} -- success`);
            //         this.webrtcConfigAndUtils.disableMediaStream(stream);
            //         break;
            //     }
            //     catch (err) {
            //         this.logger.warn(`[DeviceManagementService] testGetUserMedia error for video device ${videoInputDevices[i].deviceId} -- ${err}`);
            //     }
            // }
        }

        this.getUserMediaForVideoDone = shouldTestVideo;
        this.getUserMediaForAudioDone = shouldTestAudio;
        this.logger.info(`[DeviceManagementService] testGetUserMedia results after test: for audio ${this.getUserMediaForAudioDone} and video ${this.getUserMediaForVideoDone} -- success`);
        this.checkDeviceList();
    }

    //Check if the new plugged in device is chosen by OS as default one (default or communication)
    //for Firefox and Mac, there're no default and communication devices, so ignore if no default device is found
    private isDeviceSelectedByDefaultByOS(newDevice: MediaDeviceInfo, deviceList: MediaDeviceInfo[]): boolean {
        let isDefault = false;
        let systemHasDefaultDevices = false;
        deviceList.forEach((device) => {
            if (device.deviceId === "default" || device.deviceId === "communications") {
                systemHasDefaultDevices = true;
                if (newDevice.groupId === device.groupId && newDevice.label && device.label && device.label.indexOf(newDevice.label) !== -1) {
                    isDefault = true;
                }
            }
        });

        //if the system has no default devices, then return true by default
        if (!systemHasDefaultDevices) return true;

        return isDefault;
    }

    //check if device is one of the managed ALE devices
    //ALE devices are registered at 3200 USB number
    private isAleManagedDevice(device: MediaDeviceInfo): boolean {
        return (device.label.indexOf("3200:") !== -1 || device.label.indexOf("AH80") !== -1 || device.label.indexOf("AT10") !== -1);
        // return (device.label.indexOf("046d:") !== -1);
    }

    //update the flag if the new device is manually selected
    private updateAleManagedDeviceFlag(device: MediaDeviceInfo) {
        this.manualAleDeviceSelection = (device.deviceId !== "communications");
        this.settingsService.setSetting("manualAleDeviceSelection", String(this.manualAleDeviceSelection));
    }

    private getAleManagedDeviceFromList(newDevice: MediaDeviceInfo, devicesList: MediaDeviceInfo[]): MediaDeviceInfo {
        let deviceToReturn = newDevice;

        devicesList.forEach((device) => {
            if (device.deviceId === "communications") {
                if (newDevice.groupId === device.groupId && newDevice.label && device.label && device.label.indexOf(newDevice.label) !== -1 && !this.manualAleDeviceSelection) {
                    deviceToReturn = device;
                }
            }
        });

        this.logger.info(`[DeviceManagementService] getAleManagedDeviceFromList -- ${deviceToReturn.label} - ${deviceToReturn.deviceId}`);
        return deviceToReturn;
    }

    /*
    *
        AUDIO INPUT DEVICES
    *
    */

    /**
     * Called when we need to update the current audio input device (the device has been disconnected or null before)
     * We set the device to the "default" one by the system;
     * In case there're several devices with the same groupID, we choose the last one in the list;
     * @param tempAudioInputDevices - the list of audio input devices currently connected/detected
     */
    private updateCurrentAudioInputDevice(tempAudioInputDevices: MediaDeviceInfo[]): void {
        let defaultAudioInputDevice = null;
        tempAudioInputDevices.forEach((device) => {
            if (device.deviceId === "default") {
                defaultAudioInputDevice = device;
                if (!this.currentAudioInputDevice) { this.currentAudioInputDevice = defaultAudioInputDevice; }
            }

            //we put priority to "communications" device rather than default
            if (device.deviceId === "communications" && this.currentAudioInputDevice && this.currentAudioInputDevice.deviceId === "default") {
                this.currentAudioInputDevice = device;
            }

            if (this.currentAudioInputDevice && this.currentAudioInputDevice.groupId === device.groupId) {
                //check if we've several devices with same group id, find the one that is not "stereo"
                if (this.currentAudioInputDevice.deviceId !== "default" && this.currentAudioInputDevice.deviceId !== "communications") {
                    if (device.label.toLowerCase().indexOf("stereo") === -1) {
                        this.currentAudioInputDevice = device;
                    }
                }
                else {
                    this.currentAudioInputDevice = device;
                }
            }
        });

        if (!this.currentAudioInputDevice) {
            this.currentAudioInputDevice = tempAudioInputDevices[0];
        }
    }

    /**
     * This function watches for any changes on the audio input devices list change;
     * Could be related to the number of input devices (connected or disconnected) or change of ID on some of the devices
     * @param tempAudioInputDevices - [required] the list of audio input devices currently connected/detected
     */
    private updateCurrentAudioInputDevices(tempAudioInputDevices: MediaDeviceInfo[] = []): void {
        if (this.audioInputDevices.length !== tempAudioInputDevices.length) {
            this.logger.info(`[DeviceManagementService] updateCurrentAudioInputDevices -- current length: ${this.audioInputDevices.length} -- new length: ${tempAudioInputDevices.length}`);
        }

        if (!this.audioInputDevices.length && !tempAudioInputDevices.length) {
            //no audio devices found at all
            this.logger.info("[DeviceManagementService] checkDeviceList -- no audio input device detected !");
            return;
        }
        else if (!this.audioInputDevices.length && tempAudioInputDevices.length) {
            //no previous devices found, so probably this is the first time at start-up
            //find and select the "default" device for the computer

            this.audioInputDevices = tempAudioInputDevices;
            this.currentAudioInputDevice = null;

            const savedDeviceName = this.settingsService.getSetting("handFreeInputDeviceName");
            if (savedDeviceName) {
                //try to find the same device in the current list
                this.audioInputDevices.forEach((device) => {
                    if (device.label === savedDeviceName) {
                        //device is found
                        this.currentAudioInputDevice = device;
                        this.logger.info(`[DeviceManagementService] currentAudioInputDevice has changed (start-up) from settings -- ${savedDeviceName}`);
                    }
                });
            }

            if (!this.currentAudioInputDevice) {
                this.updateCurrentAudioInputDevice(this.audioInputDevices);

                //check for ALE specific devices
                if (this.currentAudioInputDevice && this.isAleManagedDevice(this.currentAudioInputDevice)) {
                    const newInputDevice = this.getAleManagedDeviceFromList(this.currentAudioInputDevice, this.audioInputDevices);
                    this.currentAudioInputDevice = newInputDevice;
                }
            }

            if (this.currentAudioInputDevice) {
                this.logger.info("[DeviceManagementService] currentAudioInputDevice has changed (start-up) " + this.currentAudioInputDevice.deviceId + " -- " + this.currentAudioInputDevice.label);
            }
            else {
                this.logger.info("[DeviceManagementService] currentAudioInputDevice has changed (start-up) is now null");
            }

            //send update to audio input devices list;
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audioinput", action: "added" });

            //send event for input audio device change
            this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audioinput" });
        }
        else if (this.audioInputDevices.length && !tempAudioInputDevices.length) {
            //all audio devices are disconnected
            this.audioInputDevices = [];
            this.currentAudioInputDevice = null;

            this.logger.info("[DeviceManagementService] currentAudioInputDevice is now null");

            //send event for input audio device change
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audioinput", action: "removed" });
            this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audioinput" });
        }
        else if (this.audioInputDevices.length === tempAudioInputDevices.length) {
            //check for device ID change
            let currentAudioInputDeviceChanged = false;
            let currentAudioInputDeviceListChanged = false;
            let defaultAudioInputDeviceChanged = false;
            this.audioInputDevices.forEach((inputDevice) => {
                const result = tempAudioInputDevices.find(device => inputDevice.deviceId === device.deviceId);
                if (!result) { currentAudioInputDeviceListChanged = true; }
                if (!result && inputDevice.deviceId === this.currentAudioInputDevice.deviceId) {
                    currentAudioInputDeviceChanged = true;
                }
            });

            //check for default device change
            this.audioInputDevices.forEach((inputDevice) => {
                if (inputDevice.deviceId === "default" || inputDevice.deviceId === "communications") {
                    const result = tempAudioInputDevices.find(device => inputDevice.deviceId === device.deviceId);
                    if (result && result.label !== inputDevice.label) {
                        currentAudioInputDeviceListChanged = true;
                        if (this.currentAudioInputDevice && result.deviceId === this.currentAudioInputDevice.deviceId) {
                            defaultAudioInputDeviceChanged = true;
                        }
                    }
                }
            });

            this.audioInputDevices = tempAudioInputDevices;

            //in case no update on device, update the current input device to point to the new array
            if (this.currentAudioInputDevice) {
                this.audioInputDevices.forEach((device) => {
                    if (device.deviceId === this.currentAudioInputDevice.deviceId) {
                        this.currentAudioInputDevice = device;
                    }
                });
            }

            if (currentAudioInputDeviceListChanged) {
                //update current audio device list
                this.logger.info("[DeviceManagementService] currentAudioInputDeviceListChanged has changed");
                this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audioinput", action: "updated" });
            }

            //the default device has changed from the OS
            if (defaultAudioInputDeviceChanged && !currentAudioInputDeviceChanged) {
                this.logger.info("[DeviceManagementService] currentAudioInputDevice has changed (id change) " + this.currentAudioInputDevice.deviceId + " -- " + this.currentAudioInputDevice.label);
                //update current audio input device and send update
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audioinput" });
            }

            if (currentAudioInputDeviceChanged) {
                //current audio device is no longer available, find the new default
                this.currentAudioInputDevice = null;
                this.updateCurrentAudioInputDevice(this.audioInputDevices);

                this.logger.info("[DeviceManagementService] currentAudioInputDevice has changed (id change) " + this.currentAudioInputDevice.deviceId + " -- " + this.currentAudioInputDevice.label);
                //update current audio input device and send update
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audioinput" });
            }
        }
        else if (this.audioInputDevices.length > tempAudioInputDevices.length) {
            //device(s) has been disconnected
            let currentAudioInputDeviceChanged = false;
            let defaultAudioInputDeviceChanged = false;
            this.audioInputDevices.forEach((inputDevice) => {
                const result = tempAudioInputDevices.find(device => inputDevice.deviceId === device.deviceId);
                if (!result && inputDevice.deviceId === this.currentAudioInputDevice.deviceId) {
                    currentAudioInputDeviceChanged = true;
                }
                else if (result && inputDevice.deviceId === this.currentAudioInputDevice.deviceId && result.label !== this.currentAudioInputDevice.label) {
                    defaultAudioInputDeviceChanged = true;
                }
            });

            this.audioInputDevices = tempAudioInputDevices;

            //in case no update on device, update the currnt input device to point to the new array
            if (this.currentAudioInputDevice) {
                this.audioInputDevices.forEach((device) => {
                    if (device.deviceId === this.currentAudioInputDevice.deviceId) {
                        this.currentAudioInputDevice = device;
                    }
                });
            }

            //send update to audio input devices list;
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audioinput", action: "removed" });

            if (defaultAudioInputDeviceChanged && !currentAudioInputDeviceChanged) {
                if (this.currentAudioInputDevice) {
                    this.logger.info("[DeviceManagementService] currentAudioInputDevice has changed (after current device disconnected) " + this.currentAudioInputDevice.deviceId + " -- " + this.currentAudioInputDevice.label);
                }
                //update current audio input device and send update
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audioinput", plugOut: true });
            }
            else if (currentAudioInputDeviceChanged) {
                //get the new "default" device
                this.currentAudioInputDevice = null;
                this.updateCurrentAudioInputDevice(this.audioInputDevices);

                //check for ALE specific devices
                if (this.currentAudioInputDevice && this.isAleManagedDevice(this.currentAudioInputDevice)) {
                    const newInputDevice = this.getAleManagedDeviceFromList(this.currentAudioInputDevice, this.audioInputDevices);
                    this.currentAudioInputDevice = newInputDevice;
                }

                if (this.currentAudioInputDevice) {
                    this.logger.info("[DeviceManagementService] currentAudioInputDevice has changed (after current device disconnected) " + this.currentAudioInputDevice.deviceId + " -- " + this.currentAudioInputDevice.label);
                }
                else {
                    this.logger.info("[DeviceManagementService] currentAudioInputDevice has changed (after current device disconnected) is now null");
                }

                //update current audio input device and send update
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audioinput", plugOut: true });
            }
        }
        else if (this.audioInputDevices.length < tempAudioInputDevices.length) {
            //device(s) has been connected
            let currentAudioInputDeviceChanged = false;
            //find the new device

            tempAudioInputDevices.forEach((inputDevice) => {
                const result = this.audioInputDevices.find(device => inputDevice.deviceId === device.deviceId);
                if (!result && inputDevice.deviceId !== this.currentAudioInputDevice.deviceId) {
                    //check if plugged in device is default or communication device
                    const label = inputDevice.label ? inputDevice.label.toLowerCase() : "";

                    //check for ALE specific devices
                    if (this.isAleManagedDevice(inputDevice)) {
                        const newInputDevice = this.getAleManagedDeviceFromList(inputDevice, tempAudioInputDevices);
                        this.currentAudioInputDevice = newInputDevice;
                        currentAudioInputDeviceChanged = true;
                    }
                    //if plugged-in device is default one, or a known name from the list, select it
                    else if (this.isDeviceSelectedByDefaultByOS(inputDevice, tempAudioInputDevices) || this.arrayContains(this.inputDevicesToAcceptEvenWhenNotDefault, label)) {
                        //do not select webcam micro by default
                        if (label && label.indexOf("webcam") === -1 && label.indexOf("camera") === -1 && label.indexOf("konftel cam") === -1 && label.indexOf("microsoft teams") === -1 && label.indexOf("Audio Control") === -1 && label.indexOf("zoomaudiodevice") === -1) {
                            this.currentAudioInputDevice = inputDevice;
                            currentAudioInputDeviceChanged = true;
                        }
                    }
                }
            });

            this.audioInputDevices = tempAudioInputDevices;

            //in case no update on device, update the currnt input device to point to the new array
            if (this.currentAudioInputDevice) {
                this.audioInputDevices.forEach((device) => {
                    if (device.deviceId === this.currentAudioInputDevice.deviceId) {
                        this.currentAudioInputDevice = device;
                    }
                });
            }

            //send update to audio input devices list;
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audioinput", action: "added" });

            if (currentAudioInputDeviceChanged) {
                this.logger.info("[DeviceManagementService] currentAudioInputDevice has changed (new device connected) " + this.currentAudioInputDevice.deviceId + " -- " + this.currentAudioInputDevice.label);
                this.settingsService.setSetting("handFreeInputDeviceName", this.currentAudioInputDevice.label);
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audioinput", plugIn: true });
            }
        }
    }

    /*
    *
        AUDIO OUTPUT DEVICES
    *
    */

    /**
     * Called when we need to update the current audio output device (the device has been disconnected or null before)
     * Try to find the same device as the current input device, if possible and then use it; Otherwise go to communiocations or default device;
     * @param tempAudioOutputDevices - the list of audio output devices currently connected/detected
     */
    private updateCurrentAudioOutputDevice(tempAudioOutputDevices: MediaDeviceInfo[]): void {
        let defaultAudioOutputDevice = null;

        if (this.currentAudioInputDevice && this.currentAudioInputDevice.label) {
            //try to find the same group device or name for the output
            tempAudioOutputDevices.forEach((outputDevice) => {
                //check if new device has also an input device too (so it's headphones / speakerphone)
                const groupIdFound = (this.currentAudioInputDevice.groupId === outputDevice.groupId);
                //On MAC it seems that the devices have the same name;
                //On Win they dont have the same name, but have the same "hash code" at the end of the label
                const sameNameFound = (this.currentAudioInputDevice.label === outputDevice.label || this.compareHashInLabel(this.currentAudioInputDevice.label, outputDevice.label));

                //if it's both mic and output, check also if the mic is selected currently
                if (groupIdFound || sameNameFound) {
                    this.currentAudioOutputDevice = outputDevice;
                }
            });
        }

        //not found, so go to fallback mecanism
        if (!this.currentAudioOutputDevice) {
            tempAudioOutputDevices.forEach((device) => {
                if (device.deviceId === "default") {
                    defaultAudioOutputDevice = device;
                    if (!this.currentAudioOutputDevice) { this.currentAudioOutputDevice = defaultAudioOutputDevice; }
                }

                //we put priority to "communications" device rather than default
                if (device.deviceId === "communications" && this.currentAudioOutputDevice && this.currentAudioOutputDevice.deviceId === "default") {
                    this.currentAudioOutputDevice = device;
                }

                if (this.currentAudioOutputDevice && this.currentAudioOutputDevice.groupId === device.groupId) {
                    this.currentAudioOutputDevice = device;
                }
            });
        }

        if (!this.currentAudioOutputDevice) {
            this.currentAudioOutputDevice = tempAudioOutputDevices[0];
        }

        if (this.currentAudioOutputDevice) {
            this.setGlobalTagSinkId();
        }
    }

    private async setGlobalTagSinkId(forceRefresh: boolean = false) {

        const device = this.currentAudioOutputDevice;
        const element: any = document.getElementById("globalAudioTag");

        if (!element) {
            this.logger.debug(`[DeviceManagementService] setGlobalTagSinkId -- globalAudioTag is missing`);
            return;
        }

        if (!device || !element.setSinkId) {
            this.logger.warn(`[DeviceManagementService] setGlobalTagSinkId -- no output device or setSinkId does not exist !`);
            return;
        }

        if (!forceRefresh && element.sinkId === device.deviceId) {
            this.logger.debug(`[DeviceManagementService] setGlobalTagSinkId -- Already using the same device id`);
            if (element.load) {
                element.load();
            }
            return;
        }

        try {
            this.logger.debug(`[DeviceManagementService] -- setGlobalTagSinkId to new device -- ${device.deviceId}`);
            if (element.srcObject && !element.srcObject.active) {
                this.webrtcConfigAndUtils.RTC.clearMediaStream([document.getElementById("globalAudioTag")]);
            }

            await element.setSinkId(device.deviceId);

            if (element.load) {
                element.load();
            }
        }
        catch (error) {
            this.logger.error(`[DeviceManagementService] -- setGlobalTagSinkId error -- ${error}`);

            //try to change to another device and reset to this one
            let anotherDevice = this.audioOutputDevices.find(output => (output.deviceId !== "default" && output.deviceId !== "communications" && output.deviceId !== device.deviceId));
            if (!anotherDevice) { anotherDevice = this.audioOutputDevices[0]; }

            this.logger.debug(`[DeviceManagementService] setGlobalTagSinkId -- Try to set another device ${anotherDevice.deviceId}`);
            await element.setSinkId(anotherDevice.deviceId);

            this.logger.debug(`[DeviceManagementService] setGlobalTagSinkId -- Try to set to the selected device ${device.deviceId}`);
            await element.setSinkId(device.deviceId);

            if (element.load) {
                element.load();
            }
        }
    }

    /**
     * This function watches for any changes on the audio output devices list change;
     * Could be related to the number of output devices (connected or disconnected) or change of ID on some of the devices
     * @param tempAudioOutputDevices - the list of audio output devices currently connected/detected
     */
    private updateCurrentAudioOutputDevices(tempAudioOutputDevices: MediaDeviceInfo[] = []): void {
        if (this.audioOutputDevices.length !== tempAudioOutputDevices.length) {
            this.logger.info(`[DeviceManagementService] updateCurrentAudioOutputDevices -- current length: ${this.audioOutputDevices.length} -- new length: ${tempAudioOutputDevices.length}`);
        }

        if (!this.audioOutputDevices.length && !tempAudioOutputDevices.length) {
            //no audio devices found at all
            this.logger.info("[DeviceManagementService] checkDeviceList -- no audio output device detected !");
            return;
        }
        else if (!this.audioOutputDevices.length && tempAudioOutputDevices.length) {
            //no previous devices found, so probably this is the first time at start-up
            //find and select the "default" device for the computer

            this.audioOutputDevices = tempAudioOutputDevices;
            this.currentAudioOutputDevice = null;

            const savedDeviceName = this.settingsService.getSetting("handFreeOutputDeviceName");
            if (savedDeviceName) {
                //try to find the same device in the current list
                this.audioOutputDevices.forEach((device) => {
                    if (device.label === savedDeviceName) {
                        //device is found
                        this.currentAudioOutputDevice = device;
                        this.setGlobalTagSinkId();
                        this.logger.info(`[DeviceManagementService] currentAudioOutputDevice has changed (start-up) from settings -- ${savedDeviceName}`);
                    }
                });
            }

            if (!this.currentAudioOutputDevice) {
                this.updateCurrentAudioOutputDevice(this.audioOutputDevices);

                //check for ALE specific devices
                if (this.currentAudioOutputDevice && this.isAleManagedDevice(this.currentAudioOutputDevice)) {
                    const newInputDevice = this.getAleManagedDeviceFromList(this.currentAudioOutputDevice, this.audioOutputDevices);
                    this.currentAudioOutputDevice = newInputDevice;
                }
            }

            if (this.currentAudioOutputDevice) {
                this.logger.info("[DeviceManagementService] currentAudioOutputDevice has changed (start-up) " + this.currentAudioOutputDevice.deviceId + " -- " + this.currentAudioOutputDevice.label);
            }
            else {
                this.logger.info("[DeviceManagementService] currentAudioOutputDevice has changed (start-up) is now null");
            }

            //send update to audio output devices list;
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audiooutput", action: "added" });

            //send event for output audio device change
            this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audiooutput" });

            const savedSecondaryDeviceName = this.settingsService.getSetting("ringingSpeaker");
            if (savedSecondaryDeviceName) {
                //try to find the same device in the current list
                this.logger.info(`[DeviceManagementService] (start-up) try to find secondary device with name ${savedSecondaryDeviceName}`);
                this.audioOutputDevices.forEach((device) => {
                    if (device.label === savedSecondaryDeviceName) {
                        //device is found
                        this.currentSecondaryOutputDevice = device;
                        this.logger.info(`[DeviceManagementService] currentSecondaryOutputDevice has changed (start-up) from settings ${device.label}`);
                    }
                });
            }

            if (!this.currentSecondaryOutputDevice) {
                //take the last available option, not the first one
                //but check if it's not already taken
                this.audioOutputDevices.forEach((device) => {
                    if (device.deviceId !== this.currentAudioOutputDevice.deviceId) {
                        //take the last available device from the list
                        this.currentSecondaryOutputDevice = device;
                    }
                });

                if (!this.currentSecondaryOutputDevice) { this.currentSecondaryOutputDevice = this.audioOutputDevices[this.audioOutputDevices.length - 1]; }
                this.logger.info(`[DeviceManagementService] currentSecondaryOutputDevice default value (start-up) ${this.currentSecondaryOutputDevice?.label}`);
            }

            this.sendEvent(DeviceManagementEvents.ON_NEW_SECONDARY_OUTPUT_DEVICE_SELECTED);
        }
        else if (this.audioOutputDevices.length && !tempAudioOutputDevices.length) {
            //all audio devices are disconnected
            this.audioOutputDevices = [];
            this.currentAudioOutputDevice = null;
            this.currentSecondaryOutputDevice = null;

            this.logger.info("[DeviceManagementService] currentAudioOutputDevice is now null");

            //send event for output audio device change
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audiooutput", action: "removed" });
            this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audiooutput" });
            this.sendEvent(DeviceManagementEvents.ON_NEW_SECONDARY_OUTPUT_DEVICE_SELECTED);
        }
        else if (this.audioOutputDevices.length === tempAudioOutputDevices.length) {
            //check for device ID change
            let currentAudioOutputDeviceChanged = false;
            let currentAudioOutputDeviceListChanged = false;
            let defaultAudioOutputDeviceChanged = false;
            let currentSecondaryAudioOutputDeviceChanged = false;

            this.audioOutputDevices.forEach((outputDevice) => {
                const result = tempAudioOutputDevices.find(device => outputDevice.deviceId === device.deviceId);
                if (!result) { currentAudioOutputDeviceListChanged = true; }
                if (!result && outputDevice.deviceId === this.currentAudioOutputDevice.deviceId) {
                    currentAudioOutputDeviceChanged = true;
                }
            });

            //check for default device change
            this.audioOutputDevices.forEach((outputDevice) => {
                if (outputDevice.deviceId === "default" || outputDevice.deviceId === "communications") {
                    const result = tempAudioOutputDevices.find(device => outputDevice.deviceId === device.deviceId);
                    if (result && result.label !== outputDevice.label) {
                        currentAudioOutputDeviceListChanged = true;
                        if (this.currentAudioOutputDevice && result.deviceId === this.currentAudioOutputDevice.deviceId) {
                            defaultAudioOutputDeviceChanged = true;
                        }
                        if (this.currentSecondaryOutputDevice && result.deviceId === this.currentSecondaryOutputDevice.deviceId) {
                            currentSecondaryAudioOutputDeviceChanged = true;
                        }
                    }
                }
            });

            this.audioOutputDevices = tempAudioOutputDevices;

            //in case no update on device, update the currnt output device to point to the new array
            if (this.currentAudioOutputDevice) {
                this.audioOutputDevices.forEach((device) => {
                    if (device.deviceId === this.currentAudioOutputDevice.deviceId) {
                        this.currentAudioOutputDevice = device;
                    }
                    if (this.currentSecondaryOutputDevice && device.deviceId === this.currentSecondaryOutputDevice.deviceId) {
                        this.currentSecondaryOutputDevice = device;
                    }
                });
            }

            if (currentAudioOutputDeviceListChanged) {
                //update current audio device list
                this.logger.info("[DeviceManagementService] currentAudioOutputDeviceListChanged has changed");
                this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audiooutput", action: "updated" });
            }

            if (defaultAudioOutputDeviceChanged && !currentAudioOutputDeviceChanged) {
                this.logger.info("[DeviceManagementService] currentAudioOutputDevice has changed (id change) " + this.currentAudioOutputDevice.deviceId + " -- " + this.currentAudioOutputDevice.label);
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audiooutput" });
            }

            if (currentSecondaryAudioOutputDeviceChanged) {
                this.logger.info("[DeviceManagementService] currentSecondaryAudioOutputDeviceChanged has changed (id change) " + this.currentSecondaryOutputDevice.deviceId + " -- " + this.currentSecondaryOutputDevice.label);
                this.sendEvent(DeviceManagementEvents.ON_NEW_SECONDARY_OUTPUT_DEVICE_SELECTED);
            }

            if (currentAudioOutputDeviceChanged) {
                this.currentAudioOutputDevice = null;
                this.updateCurrentAudioOutputDevice(this.audioOutputDevices);
                if (this.currentAudioOutputDevice) {
                    this.logger.info("[DeviceManagementService] currentAudioOutputDevice has changed (id change) " + this.currentAudioOutputDevice.deviceId + " -- " + this.currentAudioOutputDevice.label);
                }
                else {
                    this.logger.info("[DeviceManagementService] currentAudioOutputDevice has changed (id change) is now null");
                }

                //update current audio output device and send update
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audiooutput" });
            }
        }
        else if (this.audioOutputDevices.length > tempAudioOutputDevices.length) {
            //device(s) has been disconnected
            let currentAudioOutputDeviceChanged = false;

            //if the default device has changed, in that case send only update event
            let defaultAudioOutputDeviceChanged = false;
            let defaultSecondaryAudioOuputDeviceChanged = false;

            this.audioOutputDevices.forEach((outputDevice) => {
                const result = tempAudioOutputDevices.find(device => outputDevice.deviceId === device.deviceId);
                if (!result && outputDevice.deviceId === this.currentAudioOutputDevice.deviceId) {
                    currentAudioOutputDeviceChanged = true;
                }
                else if (result && outputDevice.deviceId === this.currentAudioOutputDevice.deviceId && result.label !== this.currentAudioOutputDevice.label) {
                    defaultAudioOutputDeviceChanged = true;
                }
            });

            let currentSecondaryAudioOutputDeviceChanged = false;
            if (this.currentSecondaryOutputDevice) {
                this.audioOutputDevices.forEach((outputDevice) => {
                    const result = tempAudioOutputDevices.find(device => outputDevice.deviceId === device.deviceId);
                    if (!result && outputDevice.deviceId === this.currentSecondaryOutputDevice.deviceId) {
                        currentSecondaryAudioOutputDeviceChanged = true;
                    }
                    else if (result && outputDevice.deviceId === this.currentSecondaryOutputDevice.deviceId && result.label !== this.currentSecondaryOutputDevice.label) {
                        defaultSecondaryAudioOuputDeviceChanged = true;
                    }
                });
            }

            this.audioOutputDevices = tempAudioOutputDevices;

            //in case no update on device, update the currnt output device to point to the new array
            if (this.currentAudioOutputDevice) {
                this.audioOutputDevices.forEach((device) => {
                    if (device.deviceId === this.currentAudioOutputDevice.deviceId) {
                        this.currentAudioOutputDevice = device;
                    }
                    if (this.currentSecondaryOutputDevice && device.deviceId === this.currentSecondaryOutputDevice.deviceId) {
                        this.currentSecondaryOutputDevice = device;
                    }
                });
            }

            //send update to audio output devices list;
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audiooutput", action: "removed" });

            //the default device that is currently used has changed
            if (defaultAudioOutputDeviceChanged && !currentAudioOutputDeviceChanged) {
                if (this.currentAudioOutputDevice) {
                    this.logger.info("[DeviceManagementService] currentAudioOutputDevice has changed (after current device disconnected) " + this.currentAudioOutputDevice.deviceId + " -- " + this.currentAudioOutputDevice.label);
                }

                //update current audio output device and send update
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audiooutput", plugOut: true });
            }
            else if (currentAudioOutputDeviceChanged) {
                this.currentAudioOutputDevice = null;
                this.updateCurrentAudioOutputDevice(this.audioOutputDevices);

                //check for ALE specific devices
                if (this.currentAudioOutputDevice && this.isAleManagedDevice(this.currentAudioOutputDevice)) {
                    const newInputDevice = this.getAleManagedDeviceFromList(this.currentAudioOutputDevice, this.audioOutputDevices);
                    this.currentAudioOutputDevice = newInputDevice;
                }

                if (this.currentAudioOutputDevice) {
                    this.logger.info("[DeviceManagementService] currentAudioOutputDevice has changed (after current device disconnected) " + this.currentAudioOutputDevice.deviceId + " -- " + this.currentAudioOutputDevice.label);
                }
                else {
                    this.logger.info("[DeviceManagementService] currentAudioOutputDevice has changed (after current device disconnected) is now null");
                }

                //update current audio output device and send update
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audiooutput", plugOut: true });
            }

            if (defaultSecondaryAudioOuputDeviceChanged && !currentSecondaryAudioOutputDeviceChanged) {
                this.sendEvent(DeviceManagementEvents.ON_NEW_SECONDARY_OUTPUT_DEVICE_SELECTED);
            }
            else if (currentSecondaryAudioOutputDeviceChanged) {
                this.currentSecondaryOutputDevice = this.audioOutputDevices[0];
                this.sendEvent(DeviceManagementEvents.ON_NEW_SECONDARY_OUTPUT_DEVICE_SELECTED);
            }
        }
        else if (this.audioOutputDevices.length < tempAudioOutputDevices.length) {
            //device(s) has been connected
            let currentAudioOutputDeviceChanged = false;
            //find the new device
            tempAudioOutputDevices.forEach((outputDevice) => {
                const result = this.audioOutputDevices.find(device => outputDevice.deviceId === device.deviceId);
                if (!result && outputDevice.deviceId !== this.currentAudioOutputDevice.deviceId) {
                    //check if new device has also an input device too (so it's headphones / speakerphone)
                    const groupIdFound = this.audioInputDevices.find((inputDevice) => {
                        return inputDevice.groupId === outputDevice.groupId;
                    });

                    //On MAC it seems that the devices have the same name;
                    //On Win they dont have the same name, but have the same "hash code" at the end of the label
                    const sameNameFound = this.audioInputDevices.find((inputDevice) => {
                        return (inputDevice.label && outputDevice.label && (inputDevice.label === outputDevice.label || this.compareHashInLabel(inputDevice.label, outputDevice.label)));
                    });

                    /* Edge Cases (in case no group ID is found) :
                        - allow also if no group ID found, but the label does not include display or screen or AMD (in case of some HDMI screen)
                        - in some other cases, the jack headset has wrong groupID
                        - basically the group ID is not reliable at all ...
                        - check if plugged in device is default for OS
                        - Also we should check for double output channel (AG Audio and Stereo when we select the output)...
                            so we should check if the current mic has AG Audio in his name and the new output has Stereo in his .. then ignore it !
                    */
                    const label = outputDevice.label ? outputDevice.label.toLowerCase() : "";

                    //check for ALE specific devices
                    if (this.isAleManagedDevice(outputDevice)) {
                        const newInputDevice = this.getAleManagedDeviceFromList(outputDevice, tempAudioOutputDevices);
                        this.currentAudioOutputDevice = newInputDevice;
                        currentAudioOutputDeviceChanged = true;
                    }
                    //if it's both mic and output, check also if the mic is selected currently
                    else if (groupIdFound || sameNameFound) {
                        if (this.currentAudioInputDevice.groupId === outputDevice.groupId || (this.currentAudioInputDevice.label && (this.currentAudioInputDevice.label === outputDevice.label || this.compareHashInLabel(this.currentAudioInputDevice.label, outputDevice.label)))) {
                            this.currentAudioOutputDevice = outputDevice;
                            currentAudioOutputDeviceChanged = true;
                        }
                    }
                    else if (this.isDeviceSelectedByDefaultByOS(outputDevice, tempAudioOutputDevices) && !this.arrayContains(this.devicesToIgnore, label)) {
                        //it's not a monitor; check for dual output case
                        //output label should be stereo and input device should contain AG Audio
                        if (label.indexOf("stereo") !== -1 && this.currentAudioInputDevice && this.currentAudioInputDevice.label && this.currentAudioInputDevice.label.toLowerCase().indexOf("ag audio") !== -1) {
                            //do nothing
                        }
                        else {
                            this.currentAudioOutputDevice = outputDevice;
                            currentAudioOutputDeviceChanged = true;
                        }
                    }

                    //if plugged-in device is known name from the list of preferred devices, select it anyway
                    if (!currentAudioOutputDeviceChanged && this.arrayContains(this.inputDevicesToAcceptEvenWhenNotDefault, label) && !this.arrayContains(this.devicesToIgnore, label)) {
                        //do not select webcam or stereo or other virtual crap
                        if (label && label.indexOf("webcam") === -1 && label.indexOf("ndi webcam") !== -1 && label.indexOf("virtual ") !== -1 && label.indexOf("camera") === -1 && label.indexOf("stereo") === -1 && label.indexOf("konftel cam") === -1 && label.indexOf("microsoft teams") === -1 && label.indexOf("Audio Control") === -1 && label.indexOf("zoomaudiodevice") === -1) {
                            this.currentAudioOutputDevice = outputDevice;
                            currentAudioOutputDeviceChanged = true;
                        }
                    }
                }
            });

            //check for change of the default device in case of current audio output device
            let defaultSecondaryAudioOuputDeviceChanged = false;
            if (this.currentSecondaryOutputDevice) {
                this.audioOutputDevices.forEach((outputDevice) => {
                    const result = tempAudioOutputDevices.find(device => outputDevice.deviceId === device.deviceId);
                    if (result && outputDevice.deviceId === this.currentSecondaryOutputDevice.deviceId && result.label !== this.currentSecondaryOutputDevice.label) {
                        defaultSecondaryAudioOuputDeviceChanged = true;
                    }
                });
            }

            this.audioOutputDevices = tempAudioOutputDevices;

            //in case no update on device, update the currnt output device to point to the new array
            if (this.currentAudioOutputDevice) {
                this.audioOutputDevices.forEach((device) => {
                    if (device.deviceId === this.currentAudioOutputDevice.deviceId) {
                        this.currentAudioOutputDevice = device;
                    }
                    if (this.currentSecondaryOutputDevice && device.deviceId === this.currentSecondaryOutputDevice.deviceId) {
                        this.currentSecondaryOutputDevice = device;
                    }
                });
            }

            //send update to audio output devices list;
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "audiooutput", action: "added" });

            if (currentAudioOutputDeviceChanged) {
                this.logger.info("[DeviceManagementService] currentAudioOutputDevice has changed (new device connected) " + this.currentAudioOutputDevice.deviceId + " -- " + this.currentAudioOutputDevice.label);
                this.settingsService.setSetting("handFreeOutputDeviceName", this.currentAudioOutputDevice.label);
                this.setGlobalTagSinkId();
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "audiooutput", plugIn: true });
            }

            if (defaultSecondaryAudioOuputDeviceChanged) {
                this.sendEvent(DeviceManagementEvents.ON_NEW_SECONDARY_OUTPUT_DEVICE_SELECTED);
            }
        }
    }

    /*
    *
        VIDEO INPUT DEVICES
    *
    */

    /**
     * Called when we need to update the current video input device (the device has been disconnected or null before)
     * @param tempVideoInputDevices - the list of video input devices currently connected/detected
     */
    private updateCurrentVideoInputDevice(tempVideoInputDevices: MediaDeviceInfo[]): void {
        let defaulVideoInputDevice = null;
        tempVideoInputDevices.forEach((device) => {
            if (device.deviceId === "default") {
                defaulVideoInputDevice = device;
                if (!this.currentVideoInputDevice) { this.currentVideoInputDevice = defaulVideoInputDevice; }
            }

            if (this.currentVideoInputDevice && this.currentVideoInputDevice.groupId === device.groupId) {
                this.currentVideoInputDevice = device;
            }
        });

        if (!this.currentVideoInputDevice) {
            this.currentVideoInputDevice = tempVideoInputDevices[0];
        }
    }

    private updateCurrentVideoInputDevices(tempVideoInputDevices: MediaDeviceInfo[] = []): void {

        if (!this.videoInputDevices.length && !tempVideoInputDevices.length) {
            //no audio devices found at all
            this.logger.info("[DeviceManagementService] checkDeviceList -- no video input device detected !");
            return;
        }

        if (this.videoInputDevices.length !== tempVideoInputDevices.length) {
            this.logger.info(`[DeviceManagementService] updateCurrentVideoInputDevices -- current length: ${this.videoInputDevices.length} -- new length: ${tempVideoInputDevices.length}`);
        }

        if (!this.videoInputDevices.length && tempVideoInputDevices.length) {
            //no previous devices found, so probably this is the first time at start-up
            //find and select the "default" device for the computer
            this.videoInputDevices = tempVideoInputDevices;
            this.currentVideoInputDevice = null;

            //if no current video device selected, try to find the one saved in the settings
            if (!this.currentVideoInputDevice) {
                const savedDeviceName = this.settingsService.getSetting("cameraUsed");
                if (savedDeviceName) {
                    //try to find the same device in the current list
                    this.videoInputDevices.forEach((device) => {
                        if (device.label === savedDeviceName) {
                            //device is found
                            this.currentVideoInputDevice = device;
                            this.logger.info("[DeviceManagementService] currentVideoInputDevice has changed from settings");
                        }
                    });
                }
            }

            if (!this.currentVideoInputDevice) { this.updateCurrentVideoInputDevice(tempVideoInputDevices); }

            if (this.currentVideoInputDevice) {
                this.logger.info("[DeviceManagementService] new currentVideoInputDevice (start-up) " + this.currentVideoInputDevice.deviceId + " -- " + this.currentVideoInputDevice.label);
            }
            else {
                this.logger.info("[DeviceManagementService] new currentVideoInputDevice (start-up) is null");
            }

            //send update to audio output devices list;
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "videoinput", action: "added" });

            //send event for output audio device change
            this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "videoinput" });
        }
        else if (this.videoInputDevices.length && !tempVideoInputDevices.length) {
            //all audio devices are disconnected
            this.videoInputDevices = [];
            this.currentVideoInputDevice = null;

            this.logger.info("[DeviceManagementService] currentVideoInputDevice is now null");

            //send event for output audio device change
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "videoinput", action: "removed" });
            this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "videoinput" });
        }
        else if (this.videoInputDevices.length > tempVideoInputDevices.length) {
            //device(s) has been disconnected
            let currentVideoInputDeviceChanged = false;
            this.videoInputDevices.forEach((videoDevice) => {
                const result = tempVideoInputDevices.find(device => videoDevice.deviceId === device.deviceId);
                if (!result && videoDevice.deviceId === this.currentVideoInputDevice.deviceId) {
                    currentVideoInputDeviceChanged = true;
                }
            });

            this.videoInputDevices = tempVideoInputDevices;

            //in case no update on device, update the currnt output device to point to the new array
            if (!currentVideoInputDeviceChanged && this.currentVideoInputDevice) {
                this.videoInputDevices.forEach((device) => {
                    if (device.deviceId === this.currentVideoInputDevice.deviceId) {
                        this.currentVideoInputDevice = device;
                    }
                });
            }

            //send update to audio output devices list;
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "videoinput", action: "removed" });

            if (currentVideoInputDeviceChanged) {
                this.currentVideoInputDevice = null;
                this.updateCurrentVideoInputDevice(tempVideoInputDevices);

                if (this.currentVideoInputDevice) {
                    this.logger.info("[DeviceManagementService] new currentVideoInputDevice has changed (after current device disconnected) " + this.currentVideoInputDevice.deviceId + " -- " + this.currentVideoInputDevice.label);
                }
                else {
                    this.logger.info("[DeviceManagementService] new currentVideoInputDevice has changed (after current device disconnected) is null");
                }

                //update current audio output device and send update
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "videoinput" });
            }
        }
        else if (this.videoInputDevices.length < tempVideoInputDevices.length) {
            //device(s) has been connected
            let currentVideoInputDeviceChanged = false;
            //find the new device
            tempVideoInputDevices.forEach((videoDevice) => {
                const result = this.videoInputDevices.find(device => videoDevice.deviceId === device.deviceId);
                if (!result && videoDevice.deviceId !== this.currentVideoInputDevice.deviceId) {
                    //do not select webcam micro by default
                    if (videoDevice.label && videoDevice.label.toLowerCase().indexOf("facetime") === -1 && videoDevice.label.toLowerCase().indexOf("virtual") === -1) {
                        this.currentVideoInputDevice = videoDevice;
                        currentVideoInputDeviceChanged = true;
                    }
                }
            });

            this.videoInputDevices = tempVideoInputDevices;

            //in case no update on device, update the current video input device to point to the new array
            if (!currentVideoInputDeviceChanged && this.currentVideoInputDevice) {
                this.videoInputDevices.forEach((device) => {
                    if (device.deviceId === this.currentVideoInputDevice.deviceId) {
                        this.currentVideoInputDevice = device;
                    }
                });
            }

            //send update to audio output devices list;
            this.sendEvent(DeviceManagementEvents.ON_AUDIO_VIDEO_DEVICE_LIST_UPDATE, { kind: "videoinput", action: "added" });

            if (currentVideoInputDeviceChanged) {
                this.logger.info("[DeviceManagementService] currentVideoInputDevice has changed (new device connected) " + this.currentVideoInputDevice.deviceId + " -- " + this.currentVideoInputDevice.label);
                this.settingsService.setSetting("cameraUsed", this.currentVideoInputDevice.label);
                this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "videoinput", plugIn: true });
            }
        }
        else if (this.videoInputDevices.length === tempVideoInputDevices.length) {
            //check for device ID change
            let currentVideoInputDeviceChanged = false;

            this.videoInputDevices.forEach((videoDevice) => {
                const result = tempVideoInputDevices.find(device => videoDevice.deviceId === device.deviceId);
                if (!result && videoDevice.deviceId === this.currentVideoInputDevice.deviceId) {
                    currentVideoInputDeviceChanged = true;
                }
            });

            this.videoInputDevices = tempVideoInputDevices;

            if (currentVideoInputDeviceChanged) {
                this.currentVideoInputDevice = null;
                this.updateCurrentVideoInputDevice(tempVideoInputDevices);

                if (this.currentVideoInputDevice) {
                    this.logger.info("[DeviceManagementService] currentVideoInputDevice has changed (change of ID) " + this.currentVideoInputDevice.deviceId + " -- " + this.currentVideoInputDevice.label);
                    this.settingsService.setSetting("cameraUsed", this.currentVideoInputDevice.label);
                    this.sendEvent(DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED, { kind: "videoinput", plugIn: true });
                }
                else {
                    this.logger.info("[DeviceManagementService] new currentVideoInputDevice has changed (after device id change) is null");
                }
            }
        }
    }
}
